import React, { useState, useRef, useEffect } from "react";
import { Plus, Search, Clock, Sparkles } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { getSuggestions, getSpellingSuggestions } from "../data/groceryDatabase";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Category badge colors (matches Home.jsx categoryConfig)
const categoryColors = {
  "Produce": "bg-green-100 text-green-700",
  "Meat & Seafood": "bg-red-100 text-red-700",
  "Dairy & Eggs": "bg-blue-100 text-blue-700",
  "Pantry": "bg-yellow-100 text-yellow-700",
  "Beverages": "bg-cyan-100 text-cyan-700",
  "Frozen": "bg-indigo-100 text-indigo-700",
  "Bakery": "bg-orange-100 text-orange-700",
  "Snacks": "bg-purple-100 text-purple-700",
  "Cereal & Breakfast": "bg-amber-100 text-amber-700",
  "Pet Food": "bg-teal-100 text-teal-700",
  "Baby": "bg-pink-100 text-pink-700",
  "Household": "bg-slate-100 text-slate-700",
  "Health & Personal Care": "bg-rose-100 text-rose-700",
};

const AutocompleteInput = ({ onAddItem, placeholder, userId }) => {
  const [input, setInput] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [smartSuggestions, setSmartSuggestions] = useState([]);
  const [showSmartSuggestions, setShowSmartSuggestions] = useState(false);
  const inputRef = useRef(null);
  const suggestionsRef = useRef(null);

  // Load smart suggestions on mount
  useEffect(() => {
    if (!userId) return;
    const fetchSmart = async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/grocery/smart-suggestions?user_id=${userId}&limit=8`);
        if (res.ok) {
          const data = await res.json();
          setSmartSuggestions(data.suggestions || []);
        }
      } catch { /* silent */ }
    };
    fetchSmart();
  }, [userId]);

  const parseQuantityFromInput = (input) => {
    const trimmedInput = input.trim();
    const match = trimmedInput.match(/^(\d+)\s+(.+)$/);
    if (match) {
      return { quantity: parseInt(match[1], 10), itemText: match[2], hasQuantity: true };
    }
    return { quantity: 1, itemText: trimmedInput, hasQuantity: false };
  };

  useEffect(() => {
    const { itemText } = parseQuantityFromInput(input);
    if (itemText.length >= 2) {
      const regularSuggestions = getSuggestions(itemText, 4);
      const spellingSuggestions = getSpellingSuggestions(itemText);
      const allSuggestions = [
        ...regularSuggestions,
        ...spellingSuggestions.filter(item => !regularSuggestions.includes(item))
      ].slice(0, 6);
      setSuggestions(allSuggestions);
      setShowSuggestions(allSuggestions.length > 0);
      setShowSmartSuggestions(false);
      setSelectedIndex(-1);
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
    }
  }, [input]);

  const handleInputChange = (e) => setInput(e.target.value);

  const handleSuggestionClick = (suggestion) => {
    const { quantity, hasQuantity } = parseQuantityFromInput(input);
    const finalInput = hasQuantity ? `${quantity} ${suggestion}` : suggestion;
    setInput("");
    setShowSuggestions(false);
    setShowSmartSuggestions(false);
    setSelectedIndex(-1);
    if (onAddItem) onAddItem(finalInput);
  };

  const handleKeyDown = (e) => {
    if (!showSuggestions) {
      if (e.key === "Enter") handleSubmit();
      return;
    }
    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setSelectedIndex(prev => prev < suggestions.length - 1 ? prev + 1 : prev);
        break;
      case "ArrowUp":
        e.preventDefault();
        setSelectedIndex(prev => prev > 0 ? prev - 1 : -1);
        break;
      case "Enter":
        e.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < suggestions.length) {
          handleSuggestionClick(suggestions[selectedIndex]);
        } else {
          handleSubmit();
        }
        break;
      case "Escape":
        setShowSuggestions(false);
        setShowSmartSuggestions(false);
        setSelectedIndex(-1);
        break;
      default: break;
    }
  };

  const handleSubmit = () => {
    if (input.trim() && onAddItem) {
      onAddItem(input.trim());
      setInput("");
      setShowSuggestions(false);
      setShowSmartSuggestions(false);
      setSelectedIndex(-1);
    }
  };

  const handleFocus = () => {
    if (input.length < 2 && smartSuggestions.length > 0) {
      setShowSmartSuggestions(true);
    }
  };

  const handleClickOutside = (e) => {
    if (
      inputRef.current && !inputRef.current.contains(e.target) &&
      suggestionsRef.current && !suggestionsRef.current.contains(e.target)
    ) {
      setShowSuggestions(false);
      setShowSmartSuggestions(false);
      setSelectedIndex(-1);
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Quick-categorize for display
  const getCategoryHint = (name) => {
    const n = name.toLowerCase();
    const brandHints = {
      "Beverages": ["faygo","pepsi","coca cola","coke","sprite","gatorade","red bull","monster","starbucks","lacroix","tropicana","oatly","silk","snapple","celsius","prime","folgers","lipton","dasani","fiji","mountain dew","dr pepper","fanta","minute maid","capri sun","arizona","vernors","sunkist"],
      "Cereal & Breakfast": ["cheerios","frosted flakes","froot loops","lucky charms","cocoa puffs","cinnamon toast crunch","raisin bran","special k","rice krispies","pop tarts","eggo","quaker","kodiak cakes","honey bunches"],
      "Snacks": ["doritos","cheetos","fritos","lay's","lays","ruffles","pringles","oreo","goldfish","cheez-it","ritz","snickers","kit kat","skittles","takis","kind bar","skinny pop"],
      "Pet Food": ["nine lives","meow mix","fancy feast","purina","iams","pedigree","blue buffalo","milk-bone","temptations","greenies","kibbles"],
      "Dairy & Eggs": ["chobani","yoplait","oikos","sargento","tillamook","kerrygold","cool whip"],
      "Frozen": ["hot pockets","stouffer","lean cuisine","digiorno","red baron","totino","ben & jerry","talenti","breyers","birds eye","ore-ida","uncrustables"],
      "Household": ["tide","gain","clorox","lysol","dawn","bounty","charmin","swiffer","glad","hefty","ziploc","febreze"],
      "Baby": ["gerber","similac","enfamil","pampers","huggies"],
    };
    for (const [cat, brands] of Object.entries(brandHints)) {
      for (const b of brands) { if (n.includes(b)) return cat; }
    }
    if (/juice|water|soda|coffee|tea|drink|lemonade/.test(n)) return "Beverages";
    if (/bread|bun|bagel|muffin|roll|croissant|donut|cake|cookie|tortilla/.test(n)) return "Bakery";
    if (/chicken|beef|fish|pork|turkey|salmon|shrimp|steak|bacon|sausage/.test(n)) return "Meat & Seafood";
    if (/apple|banana|orange|carrot|tomato|lettuce|spinach|broccoli|grape|strawberr|blueberr|avocado|mango|pineapple|cucumber|onion|potato|celery|corn|pea/.test(n)) return "Produce";
    if (/milk|cheese|yogurt|butter|egg|cream/.test(n)) return "Dairy & Eggs";
    if (/pasta|rice|flour|sugar|oil|vinegar|sauce|soup|beans|spice|honey|syrup/.test(n)) return "Pantry";
    if (/frozen|ice cream/.test(n)) return "Frozen";
    if (/chips|pretzel|popcorn|cracker|candy|chocolate|nut|jerky|granola bar/.test(n)) return "Snacks";
    return "";
  };

  return (
    <div className="relative flex-1">
      <div className="flex gap-2">
        <div className="relative flex-1">
          <Input
            ref={inputRef}
            placeholder={placeholder || "Add item (e.g., Cheerios, Faygo, milk)..."}
            value={input}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            onFocus={handleFocus}
            className="flex-1 pr-8"
            autoComplete="off"
            data-testid="add-item-input"
          />
          {input.length >= 2 && (
            <Search className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          )}
        </div>
        <Button
          onClick={handleSubmit}
          className="bg-orange-500 hover:bg-orange-600 text-white"
          disabled={!input.trim()}
          data-testid="add-item-btn"
        >
          <Plus className="h-4 w-4" />
        </Button>
      </div>

      {/* Smart Suggestions — show when input is empty/focused */}
      {showSmartSuggestions && !showSuggestions && smartSuggestions.length > 0 && (
        <div
          ref={suggestionsRef}
          className="absolute top-full left-0 right-0 z-50 bg-white border border-gray-200 rounded-md shadow-lg mt-1 max-h-72 overflow-y-auto"
          data-testid="smart-suggestions-dropdown"
        >
          <div className="px-3 py-2 bg-gradient-to-r from-amber-50 to-orange-50 border-b border-gray-100 flex items-center gap-1.5">
            <Sparkles className="h-3 w-3 text-amber-500" />
            <span className="text-xs font-medium text-amber-700">Based on your shopping history</span>
          </div>
          {smartSuggestions.map((s, i) => {
            const catColor = categoryColors[s.category] || "bg-gray-100 text-gray-600";
            return (
              <div
                key={s.name + i}
                onClick={() => handleSuggestionClick(s.name)}
                className="px-3 py-2.5 cursor-pointer hover:bg-amber-50 transition-colors flex items-center justify-between"
                data-testid={`smart-suggestion-${i}`}
              >
                <div className="flex items-center gap-2 min-w-0">
                  <Clock className="h-3 w-3 text-gray-300 flex-shrink-0" />
                  <span className="font-medium text-sm text-gray-800 truncate">{s.name}</span>
                </div>
                <span className={`text-[10px] px-1.5 py-0.5 rounded-full flex-shrink-0 ${catColor}`}>
                  {s.category}
                </span>
              </div>
            );
          })}
        </div>
      )}

      {/* Regular Suggestions Dropdown */}
      {showSuggestions && suggestions.length > 0 && (
        <div
          ref={suggestionsRef}
          className="absolute top-full left-0 right-0 z-50 bg-white border border-gray-200 rounded-md shadow-lg mt-1 max-h-60 overflow-y-auto"
          data-testid="autocomplete-dropdown"
        >
          {suggestions.map((suggestion, index) => {
            const catHint = getCategoryHint(suggestion);
            const catColor = categoryColors[catHint] || "";
            return (
              <div
                key={suggestion}
                onClick={() => handleSuggestionClick(suggestion)}
                className={`px-4 py-2 cursor-pointer transition-colors capitalize ${
                  index === selectedIndex
                    ? "bg-green-50 text-green-800 border-l-2 border-green-500"
                    : "hover:bg-gray-50 text-gray-700"
                }`}
                data-testid={`suggestion-${index}`}
              >
                <div className="flex items-center justify-between">
                  <span className="font-medium">{suggestion}</span>
                  {catHint && (
                    <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${catColor}`}>
                      {catHint}
                    </span>
                  )}
                </div>
              </div>
            );
          })}

          {/* Add custom item option */}
          {input.trim() && !suggestions.some(s => {
            const { itemText } = parseQuantityFromInput(input);
            return s.toLowerCase() === itemText.toLowerCase();
          }) && (
            <>
              <div className="border-t border-gray-100"></div>
              <div
                onClick={handleSubmit}
                className={`px-4 py-2 cursor-pointer transition-colors ${
                  selectedIndex === suggestions.length
                    ? "bg-orange-50 text-orange-800 border-l-2 border-orange-500"
                    : "hover:bg-gray-50 text-gray-700"
                }`}
              >
                <div className="flex items-center gap-2">
                  <Plus className="h-3 w-3 text-orange-500" />
                  <span className="font-medium">Add "{input}"</span>
                  <span className="text-xs text-gray-500">Custom item</span>
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default AutocompleteInput;
